import { useEffect, useState } from "react";
import axios from "axios";
import { Pencil, Trash2, Plus, X } from "lucide-react";
import { API, authHeaders, formatApiErrorDetail } from "./api";
import { AField, ATextArea, ASelect, ImageUpload, SectionShell, Notice, LoadError } from "./ui";
import { fileUrl } from "../lib/files";

const FieldInput = ({ field, value, onChange, testid }) => {
  if (field.type === "textarea")
    return <ATextArea label={field.label} testid={testid} value={value} onChange={(e) => onChange(e.target.value)} rows={4} />;
  if (field.type === "image")
    return <ImageUpload label={field.label} testid={testid} value={value} onChange={onChange} hint={field.hint} trim={!!field.trim} />;
  if (field.type === "select")
    return <ASelect label={field.label} testid={testid} value={value} onChange={(e) => onChange(e.target.value)} options={field.options} />;
  return (
    <AField
      label={field.label}
      testid={testid}
      type={field.type === "number" ? "number" : "text"}
      value={value}
      onChange={(e) => onChange(field.type === "number" ? Number(e.target.value) : e.target.value)}
    />
  );
};

const ItemForm = ({ fields, initial, onSave, onCancel, saving, testidPrefix }) => {
  const [form, setForm] = useState(initial);
  return (
    <form
      data-testid={`${testidPrefix}-form`}
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form);
      }}
      className="bg-white border border-gold/40 p-6 space-y-5"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {fields.map((f) => (
          <div key={f.key} className={f.type === "textarea" || f.type === "image" ? "sm:col-span-2" : ""}>
            <FieldInput
              field={f}
              value={form[f.key] ?? ""}
              onChange={(v) => setForm((prev) => ({ ...prev, [f.key]: v }))}
              testid={`${testidPrefix}-field-${f.key}`}
            />
          </div>
        ))}
      </div>
      <div className="flex gap-3 pt-1">
        <button type="submit" data-testid={`${testidPrefix}-save`} disabled={saving} className="btn-gold !py-3 disabled:opacity-60">
          {saving ? "Saving..." : "Save"}
        </button>
        <button type="button" data-testid={`${testidPrefix}-cancel`} onClick={onCancel} className="btn-forest !py-3 !bg-transparent !text-forest border border-forest/25">
          <X size={14} /> Cancel
        </button>
      </div>
    </form>
  );
};

export const CrudSection = ({ title, subtitle, collection, fields, emptyItem, itemTitle, itemSub, itemThumb, testidPrefix }) => {
  const [items, setItems] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");

  const load = async () => {
    setLoadError("");
    const { data } = await axios.get(`${API}/admin/${collection}`, authHeaders());
    setItems(data);
  };

  useEffect(() => {
    setItems(null);
    load().catch((e) =>
      setLoadError(formatApiErrorDetail(e.response?.data?.detail) || `This list didn't load.`)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection]);

  const save = async (form, id) => {
    setSaving(true);
    setError("");
    try {
      if (id) await axios.put(`${API}/admin/${collection}/${id}`, form, authHeaders());
      else await axios.post(`${API}/admin/${collection}`, form, authHeaders());
      setEditingId(null);
      setAdding(false);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/${collection}/${id}`, authHeaders());
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <SectionShell
      title={title}
      subtitle={subtitle}
      testid={`admin-${testidPrefix}-section`}
      action={
        <button data-testid={`${testidPrefix}-add-button`} onClick={() => { setAdding(true); setEditingId(null); }} className="btn-forest !py-3">
          <Plus size={14} /> Add New
        </button>
      }
    >
      <Notice error={error} />
      {adding && (
        <div className="mb-6">
          <ItemForm fields={fields} initial={emptyItem} onSave={(f) => save(f, null)} onCancel={() => setAdding(false)} saving={saving} testidPrefix={`${testidPrefix}-new`} />
        </div>
      )}
      {loadError ? (
        <LoadError
          testid={`${testidPrefix}-load-error`}
          onRetry={() => load().catch((e) => setLoadError(formatApiErrorDetail(e.response?.data?.detail) || `This list didn't load.`))}
          message={loadError}
        />
      ) : !items ? (
        <p className="text-sm text-sage">Loading...</p>
      ) : items.length === 0 && !adding ? (
        <p className="text-sm text-sage bg-white border border-forest/10 px-6 py-10 text-center">No entries yet — add your first one.</p>
      ) : (
        <div className="space-y-4">
          {items.map((item, i) =>
            editingId === item.id ? (
              <ItemForm
                key={item.id}
                fields={fields}
                initial={item}
                onSave={(f) => save(f, item.id)}
                onCancel={() => setEditingId(null)}
                saving={saving}
                testidPrefix={`${testidPrefix}-edit`}
              />
            ) : (
              <div key={item.id} data-testid={`${testidPrefix}-item-${i + 1}`} className="bg-white border border-forest/10 px-5 py-4 flex items-center gap-5">
                {itemThumb && itemThumb(item) && (
                  <img src={fileUrl(itemThumb(item))} alt="" className="h-14 w-20 object-cover border border-forest/10 shrink-0 bg-cream-3" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-lg text-forest truncate">{itemTitle(item)}</p>
                  {itemSub && <p className="text-[12px] text-sage truncate mt-0.5">{itemSub(item)}</p>}
                </div>
                <button
                  data-testid={`${testidPrefix}-edit-${i + 1}`}
                  onClick={() => { setEditingId(item.id); setAdding(false); }}
                  className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.12em] uppercase text-forest hover:text-gold-dark transition-colors px-3 py-2.5"
                >
                  <Pencil size={14} /> Edit
                </button>
                <button
                  data-testid={`${testidPrefix}-delete-${i + 1}`}
                  onClick={() => remove(item.id)}
                  className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.12em] uppercase text-red-700/80 hover:text-red-700 transition-colors px-3 py-2.5"
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            )
          )}
        </div>
      )}
    </SectionShell>
  );
};
